'use client';

import { cn } from '@/lib/utils';
import { Bookmark, LoaderCircle } from 'lucide-react';
import type { ButtonHTMLAttributes, MouseEvent, ReactNode } from 'react';

type BookmarkButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  isFollowed?: boolean;
  isFollowing?: boolean;
  toggleFollow?: () => void | Promise<void>;
  description?: ReactNode;
};

export default function BookmarkButton({
  isFollowed,
  isFollowing,
  toggleFollow,
  description,
  onClick,
  className,
  disabled,
  'aria-label': ariaLabel,
  ...buttonProps
}: BookmarkButtonProps) {
  async function handleClick(event: MouseEvent<HTMLButtonElement>) {
    event.preventDefault();
    event.stopPropagation();
    if (onClick) {
      onClick(event);
      return;
    }

    await toggleFollow?.();
  }

  return (
    <button
      type="button"
      aria-label={ariaLabel ?? (isFollowed ? 'Remove bookmark' : 'Add bookmark')}
      aria-busy={isFollowing || undefined}
      disabled={isFollowing || disabled}
      onClick={handleClick}
      {...buttonProps}
      className={cn(
        'flex shrink-0 items-center justify-center rounded-lg border border-[#3A3838] transition-colors hover:bg-white/4 disabled:cursor-not-allowed disabled:opacity-60',
        description ? 'h-8 gap-1.5 px-3 text-sm text-[#BFBFBF]' : 'size-8',
        className,
      )}
    >
      {isFollowing ? (
        <LoaderCircle size={20} className="animate-spin text-[#F9D4AF]" />
      ) : (
        <Bookmark
          size={20}
          stroke={isFollowed ? '#F9D4AF' : '#BFBFBF'}
          fill={isFollowed ? '#F9D4AF' : 'transparent'}
        />
      )}
      {description ? <span>{description}</span> : null}
    </button>
  );
}
